/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { InitiativeUTRLookup } from '../types/initiative';
import G17Client from '../services/G17Client';
import { handleRouteError } from '../logger';
import { ThunkAction } from 'redux-thunk';
import { DataPeriods } from '@utils/dataPeriods';
import { RootState } from '../reducers';

export interface Item {
  id: string;
  loaded?: boolean,
  loading?: boolean,
  error?: boolean,
  data?: InitiativeUTRLookup
}

const referenceUtrAdapter = createEntityAdapter({
  selectId: (item: Item) => item.id,
})

const referenceUtrSlice = createSlice({
  name: 'referenceUtr',
  initialState: referenceUtrAdapter.getInitialState(),
  reducers: {
    add: referenceUtrAdapter.addOne,
    update: referenceUtrAdapter.upsertOne,
  }
})

const { add, update } = referenceUtrSlice.actions

// Can create a set of memoized selectors based on the location of this entity state
export const referenceUtrSelectors = referenceUtrAdapter.getSelectors((state: RootState) => state.referenceUtr)

export default referenceUtrSlice.reducer

const notFound = (id: string) => ({
  id,
  loaded: true,
  loading: false,
  error: true,
});

type LoadReferenceUtrParams = { initiativeId: string, utrCode: string, period?: DataPeriods };

export const generateReferenceUtrId = ({ initiativeId, utrCode, period }: LoadReferenceUtrParams) => {
  return `${initiativeId}-${utrCode}-${period ?? 'none'}`;
}

export const loadReferenceUtr = (props: LoadReferenceUtrParams): ThunkAction<void, any, unknown, { type: string }> => {
  return async (dispatch, getState) => {

    const { initiativeId, utrCode, period } = props;
    const id = generateReferenceUtrId(props);

    const referenceUtr = referenceUtrSelectors.selectById(getState(), id);
    if (referenceUtr && (referenceUtr.loaded || referenceUtr.loading)) {
      return;
    }

    dispatch(add({ id, loading: true }));
    G17Client.loadUniversalTrackerByCode(initiativeId, utrCode, period ?? DataPeriods.Yearly)
      .then(
        (data: InitiativeUTRLookup) => {
          dispatch(update({ id, data, loaded: true, loading: false }))
        },
        (err: Error) => {
          handleRouteError(err, { initiativeId, utrCode })
          dispatch(update(notFound(id)));
        }
      )
  };
}
